import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import toast, { Toaster } from 'react-hot-toast'
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaFacebook, FaYoutube, FaInstagram, FaPaperPlane } from 'react-icons/fa'
import AOS from 'aos'
import 'aos/dist/aos.css'
import useDocumentTitle from '../../hooks/useDocumentTitle'
import './LienHe.css'

interface FormData {
  name: string
  email: string
  phone: string
  service: string
  message: string
}

const LienHePage = () => {
  useDocumentTitle('Liên Hệ - MAXCONST')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>()

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      easing: 'ease-out-cubic'
    })
  }, [])

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true)
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      console.log('Form data:', data)
      toast.success('Gửi thông tin thành công! Chúng tôi sẽ liên hệ lại với bạn sớm nhất.', {
        duration: 4000,
        position: 'top-center',
        icon: '✅',
      })
      reset()
    } catch (error) {
      toast.error('Có lỗi xảy ra. Vui lòng thử lại sau!', {
        duration: 4000,
        position: 'top-center',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const contactInfo = [
    {
      icon: <FaPhone />,
      title: 'Điện thoại',
      content: '0979 808 278',
      link: 'tel:0979 808 278'
    },
    {
      icon: <FaEnvelope />,
      title: 'Email',
      content: 'vpmaxconst@gmail.com',
      link: 'mailto:vpmaxconst@gmail.com'
    },
    {
      icon: <FaMapMarkerAlt />,
      title: 'Địa chỉ',
      content: '37 Đường D3, Khu TĐC Hố Lang, Kp Tân Hiệp, Phường Tân Đông Hiệp, Thành phố Dĩ An, Tỉnh Bình Dương',
      link: 'https://maps.app.goo.gl/nePnYwz8M817vinB7'
    },
    {
      icon: <FaClock />,
      title: 'Giờ làm việc',
      content: 'T2 - T7: 8:00 - 18:00',
      link: '#'
    }
  ]

  const services = [
    'Thiết kế kiến trúc',
    'Thi công xây dựng',
    'Thiết kế nội thất',
    'Tư vấn dự án',
    'Khác'
  ]

  return (
    <div className="lien-he-page">
      <Toaster />
      
      {/* Hero Section */}
      <motion.div 
        className="lien-he-hero"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <h1>Liên Hệ Với Chúng Tôi</h1>
        <p>Hãy để MaxConst đồng hành cùng bạn trong hành trình kiến tạo không gian sống hoàn hảo</p>
      </motion.div>

      {/* Contact Info Cards */}
      <div className="contact-info-grid" data-aos="fade-up">
        {contactInfo.map((info, index) => (
          <motion.a
            key={index}
            href={info.link}
            className="contact-info-card"
            whileHover={{ scale: 1.05, y: -5 }}
            whileTap={{ scale: 0.95 }}
            data-aos="zoom-in"
            data-aos-delay={index * 100}
          >
            <div className="contact-icon">{info.icon}</div>
            <h3>{info.title}</h3>
            <p>{info.content}</p>
          </motion.a>
        ))}
      </div>

      {/* Main Content */}
      <div className="contact-main-content">
        {/* Contact Form */}
        <motion.div 
          className="contact-form-section"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          data-aos="fade-right"
        >
          <h2>Gửi Thông Tin Liên Hệ</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="contact-form">
            <div className="form-group">
              <label htmlFor="name">Họ và tên *</label>
              <input
                type="text"
                id="name"
                placeholder="Nguyễn Văn A"
                {...register('name', { 
                  required: 'Vui lòng nhập họ tên',
                  minLength: { value: 2, message: 'Họ tên phải có ít nhất 2 ký tự' }
                })}
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-message">{errors.name.message}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  placeholder="email@example.com"
                  {...register('email', { 
                    required: 'Vui lòng nhập email',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Email không hợp lệ'
                    }
                  })}
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-message">{errors.email.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Số điện thoại *</label>
                <input
                  type="tel"
                  id="phone"
                  placeholder="0123456789"
                  {...register('phone', { 
                    required: 'Vui lòng nhập số điện thoại',
                    pattern: {
                      value: /^[0-9]{10,11}$/,
                      message: 'Số điện thoại không hợp lệ'
                    }
                  })}
                  className={errors.phone ? 'error' : ''}
                />
                {errors.phone && <span className="error-message">{errors.phone.message}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="service">Dịch vụ quan tâm *</label>
              <select
                id="service"
                {...register('service', { required: 'Vui lòng chọn dịch vụ' })}
                className={errors.service ? 'error' : ''}
              >
                <option value="">-- Chọn dịch vụ --</option>
                {services.map((service, index) => (
                  <option key={index} value={service}>{service}</option>
                ))}
              </select>
              {errors.service && <span className="error-message">{errors.service.message}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="message">Nội dung *</label>
              <textarea
                id="message"
                rows={5}
                placeholder="Vui lòng mô tả chi tiết yêu cầu của bạn..."
                {...register('message', { 
                  required: 'Vui lòng nhập nội dung',
                  minLength: { value: 10, message: 'Nội dung phải có ít nhất 10 ký tự' }
                })}
                className={errors.message ? 'error' : ''}
              ></textarea>
              {errors.message && <span className="error-message">{errors.message.message}</span>}
            </div>

            <motion.button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isSubmitting ? (
                <span className="loading-text">
                  <span className="spinner"></span>
                  Đang gửi...
                </span>
              ) : (
                <span className="button-content">
                  <FaPaperPlane />
                  Gửi thông tin
                </span>
              )}
            </motion.button>
          </form>
        </motion.div>

        {/* Map & Social Section */}
        <motion.div 
          className="map-social-section"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          data-aos="fade-left"
        >
          {/* Map */}
          <div className="map-container">
            <h2>Vị Trí Của Chúng Tôi</h2>
            <div className="map-wrapper">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d3917.081820113446!2d106.7503034!3d10.9572087!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3174d9007a7cebdd%3A0xa93c04caf93dbb35!2zQ8OUTkcgVFkgVE5ISCBUSIBUSVQgS0UgVsOAIFjDglkgRFnhuKRORyBUUsG7Q04gR8G7WEkgTUFYQ09OU1Q!5e0!3m2!1svi!2s!4v1780854000000!5m2!1svi!2s"
                width="100%"
                height="350"
                style={{ border: 0, borderRadius: '12px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="MaxConst Location"
              ></iframe>
            </div>
          </div>

          {/* Social Media */}
          <div className="social-section">
            <h2>Kết Nối Với Chúng Tôi</h2>
            <p>Theo dõi chúng tôi để cập nhật những dự án mới nhất</p>
            <div className="social-links">
              <motion.a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="social-btn facebook"
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaFacebook />
                <span>Facebook</span>
              </motion.a>
              <motion.a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                className="social-btn youtube"
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaYoutube />
                <span>YouTube</span>
              </motion.a>
              <motion.a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="social-btn instagram"
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaInstagram />
                <span>Instagram</span>
              </motion.a>
            </div>
          </div>

          {/* Info Box */}
          <motion.div 
            className="info-box"
            whileHover={{ scale: 1.02 }}
          >
            <h3>💡 Tại sao chọn MaxConst?</h3>
            <ul>
              <li>✅ Đội ngũ kiến trúc sư chuyên nghiệp</li>
              <li>✅ Tư vấn miễn phí 24/7</li>
              <li>✅ Báo giá minh bạch, hợp lý</li>
              <li>✅ Bảo hành dài hạn</li>
              <li>✅ Hơn 500+ dự án hoàn thành</li>
            </ul>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}

export default LienHePage
